import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import tenantService from '../services/tenantService';

const CurrencyContext = createContext();
const DEFAULT_CURRENCY = 'HTG';

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(
    localStorage.getItem('tenant_currency') || DEFAULT_CURRENCY
  );

  const applyCurrency = useCallback((value) => {
    const nextCurrency = value || DEFAULT_CURRENCY;
    localStorage.setItem('tenant_currency', nextCurrency);
    setCurrency(nextCurrency);
  }, []);

  const refreshCurrency = useCallback(async () => {
    if (!localStorage.getItem('access_token')) return;

    try {
      const tenant = await tenantService.getCurrentTenant();
      applyCurrency(tenant?.currency);
    } catch (error) {
      applyCurrency(localStorage.getItem('tenant_currency') || DEFAULT_CURRENCY);
    }
  }, [applyCurrency]);

  useEffect(() => {
    refreshCurrency();

    const handleCurrencyChange = (event) => {
      applyCurrency(event.detail?.currency);
    };

    window.addEventListener('tenant-currency-changed', handleCurrencyChange);
    return () => window.removeEventListener('tenant-currency-changed', handleCurrencyChange);
  }, [applyCurrency, refreshCurrency]);

  const formatCurrency = useCallback((value) => {
    const amount = Number(value || 0);
    return `${amount.toLocaleString('fr-FR')} ${currency}`;
  }, [currency]);

  const value = useMemo(() => ({
    currency,
    setCurrency: applyCurrency,
    refreshCurrency,
    formatCurrency,
  }), [applyCurrency, currency, formatCurrency, refreshCurrency]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
